import { PspContext } from "@/config/contexts";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "@/microsoftAuthConfig";
import {
  getSsoRedirectToStorage,
  setAccessTokenStorage,
  setRefreshTokenStorage,
  setSelectedMandateIdStorage,
  setSsoRedirectToStorage,
} from "@/config/utils";
import { loginSso, SsoCredentials } from "@/api/openapi";
import { translate } from "@/config/translator";
import { setMsalInstance } from "@/config/msalTokenProvider";

export function MicrosoftSsoLogin() {
  const navigate = useNavigate();
  const {
    setUser,
    setLang,
    selectedMandateId,
    setSelectedMandateId,
    setSelectedMandate,
    setCookieConsentDialogOpen,
    lang,
  } = useContext(PspContext);

  const { instance, accounts, inProgress } = useMsal();
  const activeAccount = instance.getActiveAccount() ?? accounts[0] ?? null;

  useEffect(() => {
    setMsalInstance(instance);
  }, [instance]);

  useEffect(() => {
    // Do not start any interactive flow while MSAL reports an interaction in progress
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    const loginAndCallApi = async () => {
      // 1) No account -> interactive login
      if (!activeAccount) {
        try {
          await instance.loginRedirect({
            ...loginRequest,
            prompt: "select_account",
          });
        } catch (e) {
          console.error(e);
        }
        return;
      }

      // 2) We have an account -> try to get a token silently
      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account: activeAccount,
          forceRefresh: true,
        });

        const credentials: SsoCredentials = {
          // backend usually should use accessToken, but you currently use idToken:
          token: result.idToken,
        };

        const { data } = await loginSso({ body: credentials });
        if (!data) throw new Error("Could not login with SSO.");

        setAccessTokenStorage(data.accessToken);
        setRefreshTokenStorage(data.refreshToken);
        setUser(data.user);
        setLang(data.user.language.short);

        if (!selectedMandateId) {
          const firstMandate = data.user.mandateRights[0]?.mandate ?? null;

          if (firstMandate) {
            setSelectedMandateId(firstMandate.id);
            setSelectedMandate(firstMandate);

            setSelectedMandateIdStorage(firstMandate.id);
          }
        }

        const redirectTo = getSsoRedirectToStorage() || "/home";
        setSsoRedirectToStorage(null);
        navigate(redirectTo);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        // 3) Silent token failed, maybe interaction required
        if (
          e.errorCode === "interaction_required" ||
          e.name === "InteractionRequiredAuthError"
        ) {
          // Guard again: only start interactive login if none is running
          if (inProgress === InteractionStatus.None) {
            try {
              await instance.loginRedirect({
                ...loginRequest,
                prompt: "select_account",
              });
            } catch (err) {
              console.error(err);
            }
          }
        } else {
          console.error(e);
        }
      }
    };

    loginAndCallApi();
  }, [
    activeAccount,
    inProgress,
    instance,
    navigate,
    selectedMandateId,
    setCookieConsentDialogOpen,
    setLang,
    setSelectedMandate,
    setSelectedMandateId,
    setUser,
  ]);

  const handleRedirect = () => {
    // Also prevent manual double-interaction from the button
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    instance
      .loginRedirect({
        ...loginRequest,
        // "create" is not a standard prompt, but keeping your value here.
        prompt: "create",
      })
      .catch((error) => console.error(error));
  };

  return (
    <div className="App flex justify-center items-center absolute inset-0">
      {/* LOGO */}
      <div className="absolute top-16 tablet:top-20 w-full flex flex-row justify-center">
        <img src="/logo.svg" className="w-50 tablet:w-80" />
      </div>

      {/* WHEN AUTHENTICATED */}
      <AuthenticatedTemplate>
        {activeAccount ? (
          <div className="text-center">
            <h1 className="font-semibold text-3xl">
              {translate("welcomeComma", lang)} {activeAccount.name}!
            </h1>
            <p className="text-xl">
              {translate("successfullyLoggedInMicrosoftLogin", lang)}
            </p>
          </div>
        ) : null}
      </AuthenticatedTemplate>

      {/* WHEN NOT AUTHENTICATED */}
      <UnauthenticatedTemplate>
        <div className="text-center">
          <h1 className="text-3xl mb-4">{translate("microsoftLogin", lang)}</h1>
          <p className="text-xl">
            {translate("youAreBeingRedirectedMicrosoftLogin", lang)}
          </p>
          <button
            onClick={handleRedirect}
            className="cursor-pointer text-primary font-semibold mt-16 text-xl"
          >
            {translate("ifNotRedirectedClickHere", lang)}
          </button>
        </div>
      </UnauthenticatedTemplate>
    </div>
  );
}
